import React, { useState, useEffect } from 'react';
import { collection, getDocs, updateDoc, doc, writeBatch } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useUser } from '../context/UserContext';
import Swal from 'sweetalert2';

interface Property {
  id: string;
  nombre: string;
  ciudad?: string;
  codigo_zip?: string;
  tipo?: string;
  inmobiliaria?: string;
  inmobiliaria_id?: string;
  agencia_info?: {
    inmobiliaria_id?: string;
    nombre?: string;
  };
  agente_id?: string;
  agente_nombre?: string;
  asesor_id?: string[];
  precio?: number;
  list_number?: string;
  titulo?: string;
  direccion?: any;
  precio_actual_usd?: number;
  precio_original_usd?: number;
}

interface Agency {
  id: string;
  inmobiliaria_id: string;
  nombre: string;
  ciudad?: string;
  correo?: string;
  telefono?: string;
}

interface Agent {
  id: string;
  nombre: string;
  correo: string;
  inmobiliaria_id?: string;
  tipo?: string;
}

export default function PropertyAgencyAssigner() {
  const { user } = useUser();
  const [properties, setProperties] = useState<Property[]>([]);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  // Filtros
  const [filtro, setFiltro] = useState('');
  const [filtroCiudad, setFiltroCiudad] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroEstado, setFiltroEstado] = useState<'todas' | 'con_agencia' | 'sin_agencia'>('sin_agencia');

  // Selección
  const [selectedAgency, setSelectedAgency] = useState<Agency | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [agencyAgents, setAgencyAgents] = useState<Agent[]>([]);

  // Estadísticas
  const [assignedCount, setAssignedCount] = useState(0);

  useEffect(() => {
    loadData();
  }, []);

  // Cargar agentes cuando se selecciona una agencia
  useEffect(() => {
    if (selectedAgency) {
      const agentsOfAgency = agents.filter(a =>
        a.inmobiliaria_id === selectedAgency.inmobiliaria_id ||
        a.inmobiliaria_id === selectedAgency.id
      );
      setAgencyAgents(agentsOfAgency);
    } else {
      setAgencyAgents([]);
      setSelectedAgent(null);
    }
  }, [selectedAgency, agents]);

  const loadData = async () => {
    try {
      // Cargar propiedades
      const propsSnapshot = await getDocs(collection(db, 'propiedades'));
      const props: Property[] = [];
      propsSnapshot.forEach(doc => {
        const data = doc.data();
        props.push({
          id: doc.id,
          nombre: data.nombre || data.titulo || 'Sin nombre',
          titulo: data.titulo,
          ciudad: data.ciudad,
          codigo_zip: data.codigo_zip,
          tipo: data.tipo,
          inmobiliaria: data.inmobiliaria,
          inmobiliaria_id: data.inmobiliaria_id,
          agencia_info: data.agencia_info,
          agente_id: data.agente_id,
          agente_nombre: data.agente_nombre,
          asesor_id: data.asesor_id,
          precio: data.precio,
          precio_actual_usd: data.precio_actual_usd,
          precio_original_usd: data.precio_original_usd,
          direccion: data.direccion,
          list_number: data.list_number
        });
      });

      // Cargar agencias
      const agenciesSnapshot = await getDocs(collection(db, 'inmobiliaria'));
      const agenciesList: Agency[] = [];
      agenciesSnapshot.forEach(doc => {
        const data = doc.data();
        agenciesList.push({
          id: doc.id,
          inmobiliaria_id: data.inmobiliaria_id || doc.id,
          nombre: data.nombre || 'Sin nombre',
          ciudad: data.ciudad,
          correo: data.correo,
          telefono: data.telefono
        });
      });

      // Cargar agentes (asesores y asociados)
      const agentsList: Agent[] = [];
      const asesoresSnapshot = await getDocs(collection(db, 'asesor'));
      asesoresSnapshot.forEach(doc => {
        const data = doc.data();
        agentsList.push({
          id: doc.id,
          nombre: data.nombre || 'Sin nombre',
          correo: data.correo || '',
          inmobiliaria_id: data.inmobiliaria_id,
          tipo: 'Afiliado'
        });
      });

      const asociadosSnapshot = await getDocs(collection(db, 'asociado'));
      asociadosSnapshot.forEach(doc => {
        const data = doc.data();
        agentsList.push({
          id: doc.id,
          nombre: data.nombre || 'Sin nombre',
          correo: data.correo || '',
          inmobiliaria_id: data.inmobiliaria_id,
          tipo: 'Asociado'
        });
      });

      setProperties(props.sort((a, b) => a.nombre.localeCompare(b.nombre)));
      setAgencies(agenciesList.sort((a, b) => a.nombre.localeCompare(b.nombre)));
      setAgents(agentsList.sort((a, b) => a.nombre.localeCompare(b.nombre)));
    } catch (error) {
      console.error('Error cargando datos:', error);
      Swal.fire('Error', 'No se pudieron cargar los datos', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Solo admins pueden usar este componente
  if (!user || !user.tipo?.includes('admin')) {
    return (
      <div className="alert alert-danger">
        <h6>Acceso Denegado</h6>
        <p>Solo administradores pueden gestionar propiedades.</p>
      </div>
    );
  }

  // Obtener ciudades únicas
  const getCiudadesUnicas = () => {
    const ciudades = new Set<string>();
    properties.forEach(p => {
      if (p.ciudad) ciudades.add(p.ciudad);
    });
    return Array.from(ciudades).sort();
  };

  // Obtener tipos únicos
  const getTiposUnicos = () => {
    const tipos = new Set<string>();
    properties.forEach(p => {
      if (p.tipo) tipos.add(p.tipo);
    });
    return Array.from(tipos).sort();
  };

  // Aplicar filtros
  let propiedadesFiltradas = properties;

  if (filtro.trim() !== '') {
    propiedadesFiltradas = propiedadesFiltradas.filter(p =>
      p.nombre.toLowerCase().includes(filtro.toLowerCase()) ||
      (p.list_number && p.list_number.includes(filtro)) ||
      (p.ciudad && p.ciudad.toLowerCase().includes(filtro.toLowerCase()))
    );
  }

  if (filtroCiudad) {
    propiedadesFiltradas = propiedadesFiltradas.filter(p => p.ciudad === filtroCiudad);
  }

  if (filtroTipo) {
    propiedadesFiltradas = propiedadesFiltradas.filter(p => p.tipo === filtroTipo);
  }

  if (filtroEstado === 'con_agencia') {
    propiedadesFiltradas = propiedadesFiltradas.filter(p => {
      const hasAgency = p.inmobiliaria || p.inmobiliaria_id || p.agencia_info?.inmobiliaria_id;
      return hasAgency;
    });
  } else if (filtroEstado === 'sin_agencia') {
    propiedadesFiltradas = propiedadesFiltradas.filter(p => {
      const hasAgency = p.inmobiliaria || p.inmobiliaria_id || p.agencia_info?.inmobiliaria_id;
      return !hasAgency;
    });
  }

  // Asignar agencia individual
  const handleAssignSingle = async (property: Property) => {
    if (!selectedAgency) {
      Swal.fire('Error', 'Selecciona una agencia primero', 'error');
      return;
    }

    const confirm = await Swal.fire({
      title: 'Asignar Agencia',
      html: `
        <div class="text-start">
          <p>Se asignará:</p>
          <p><strong>Propiedad:</strong> ${property.nombre}</p>
          <p><strong>Agencia:</strong> ${selectedAgency.nombre}</p>
          ${selectedAgent ? `<p><strong>Agente:</strong> ${selectedAgent.nombre}</p>` : ''}
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, asignar',
      cancelButtonText: 'Cancelar'
    });

    if (!confirm.isConfirmed) return;

    setProcessing(true);
    try {
      const updateData: any = {
        inmobiliaria: selectedAgency.nombre,
        inmobiliaria_id: selectedAgency.inmobiliaria_id
      };

      if (selectedAgent) {
        updateData.agente_id = selectedAgent.correo;
        updateData.agente_nombre = selectedAgent.nombre;
      }

      await updateDoc(doc(db, 'propiedades', property.id), updateData);

      // Actualizar estado local
      setProperties(prev => prev.map(p =>
        p.id === property.id ? {...p, ...updateData} : p
      ));

      setAssignedCount(prev => prev + 1);
      Swal.fire('Éxito', 'Propiedad asignada correctamente', 'success');
    } catch (error) {
      console.error('Error asignando propiedad:', error);
      Swal.fire('Error', 'No se pudo asignar la propiedad', 'error');
    } finally {
      setProcessing(false);
    }
  };

  // Asignar masivamente
  const handleBulkAssign = async () => {
    const propiedadesSinAgencia = propiedadesFiltradas.filter(p => {
      const hasAgency = p.inmobiliaria || p.inmobiliaria_id || p.agencia_info?.inmobiliaria_id;
      return !hasAgency;
    });

    if (!selectedAgency) {
      Swal.fire('Error', 'Selecciona una agencia primero', 'error');
      return;
    }

    if (propiedadesSinAgencia.length === 0) {
      Swal.fire('Info', 'No hay propiedades sin agencia para asignar', 'info');
      return;
    }

    const confirm = await Swal.fire({
      title: 'Asignación Masiva',
      html: `
        <div class="text-start">
          <p>Se asignarán <strong>${propiedadesSinAgencia.length}</strong> propiedades a:</p>
          <p><strong>Agencia:</strong> ${selectedAgency.nombre}</p>
          ${selectedAgent ? `<p><strong>Agente:</strong> ${selectedAgent.nombre}</p>` : ''}
          <p class="text-warning mt-3">Esta acción no se puede deshacer.</p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, asignar todas',
      cancelButtonText: 'Cancelar'
    });

    if (!confirm.isConfirmed) return;

    setProcessing(true);
    const batch = writeBatch(db);
    let count = 0;

    try {
      for (const property of propiedadesSinAgencia) {
        const propRef = doc(db, 'propiedades', property.id);
        const updateData: any = {
          inmobiliaria: selectedAgency.nombre,
          inmobiliaria_id: selectedAgency.inmobiliaria_id
        };

        if (selectedAgent) {
          updateData.agente_id = selectedAgent.correo;
          updateData.agente_nombre = selectedAgent.nombre;
        }

        batch.update(propRef, updateData);
        count++;

        // Commit cada 100 operaciones
        if (count % 100 === 0) {
          await batch.commit();
        }
      }

      await batch.commit();

      // Recargar datos
      await loadData();

      setAssignedCount(prev => prev + count);

      Swal.fire({
        title: 'Asignación Completada',
        html: `
          <div class="text-start">
            <p><strong>✅ ${count} propiedades asignadas exitosamente</strong></p>
            <p>Agencia: ${selectedAgency.nombre}</p>
            ${selectedAgent ? `<p>Agente: ${selectedAgent.nombre}</p>` : ''}
          </div>
        `,
        icon: 'success'
      });
    } catch (error) {
      console.error('Error en asignación masiva:', error);
      Swal.fire('Error', 'Error durante la asignación masiva', 'error');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
        <p className="mt-2">Cargando propiedades y agencias...</p>
      </div>
    );
  }

  // Estadísticas - usar la misma lógica que PropertyAnalyzer
  const stats = {
    total: properties.length,
    conAgencia: properties.filter(p => {
      const hasAgency = p.inmobiliaria || p.inmobiliaria_id || p.agencia_info?.inmobiliaria_id;
      return hasAgency;
    }).length,
    sinAgencia: properties.filter(p => {
      const hasAgency = p.inmobiliaria || p.inmobiliaria_id || p.agencia_info?.inmobiliaria_id;
      return !hasAgency;
    }).length,
    conAgente: properties.filter(p => {
      return p.agente_id || (p.asesor_id && Array.isArray(p.asesor_id) && p.asesor_id.length > 0);
    }).length
  };

  return (
    <div className="property-agency-assigner">
      <div className="card">
        <div className="card-header bg-primary text-white">
          <h5 className="mb-0">
            <i className="fas fa-building me-2"></i>
            Asignación de Agencias a Propiedades
          </h5>
        </div>
        <div className="card-body">
          {/* Estadísticas */}
          <div className="row mb-4">
            <div className="col-md-2">
              <div className="card text-center bg-info text-white">
                <div className="card-body">
                  <h3>{stats.total}</h3>
                  <p className="mb-0">Total</p>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className="card text-center bg-success text-white">
                <div className="card-body">
                  <h3>{stats.conAgencia}</h3>
                  <p className="mb-0">Con Agencia</p>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className="card text-center bg-danger text-white">
                <div className="card-body">
                  <h3>{stats.sinAgencia}</h3>
                  <p className="mb-0">Sin Agencia</p>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className="card text-center bg-primary text-white">
                <div className="card-body">
                  <h3>{stats.conAgente}</h3>
                  <p className="mb-0">Con Agente</p>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className="card text-center bg-warning text-white">
                <div className="card-body">
                  <h3>{Math.round((stats.sinAgencia / stats.total) * 100)}%</h3>
                  <p className="mb-0">Por Asignar</p>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className="card text-center bg-purple text-white" style={{backgroundColor: '#6f42c1'}}>
                <div className="card-body">
                  <h3>{assignedCount}</h3>
                  <p className="mb-0">Asignadas Hoy</p>
                </div>
              </div>
            </div>
          </div>

          {/* Panel de Selección de Agencia */}
          <div className="card mb-4">
            <div className="card-header">
              <h6><i className="fas fa-home me-2"></i>Seleccionar Agencia y Agente</h6>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <label className="form-label">Agencia:</label>
                  <select
                    className="form-select"
                    value={selectedAgency?.id || ''}
                    onChange={(e) => {
                      const agency = agencies.find(a => a.id === e.target.value);
                      setSelectedAgency(agency || null);
                    }}
                  >
                    <option value="">-- Seleccionar Agencia --</option>
                    {agencies.map(agency => (
                      <option key={agency.id} value={agency.id}>
                        {agency.nombre} {agency.ciudad ? `(${agency.ciudad})` : ''}
                      </option>
                    ))}
                  </select>
                  {selectedAgency && (
                    <small className="text-muted">
                      ID: {selectedAgency.inmobiliaria_id} | {agencyAgents.length} agentes
                    </small>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Agente (Opcional):</label>
                  <select
                    className="form-select"
                    value={selectedAgent?.id || ''}
                    onChange={(e) => {
                      const agent = agencyAgents.find(a => a.id === e.target.value);
                      setSelectedAgent(agent || null);
                    }}
                    disabled={!selectedAgency}
                  >
                    <option value="">-- Sin Agente Específico --</option>
                    {agencyAgents.map(agent => (
                      <option key={agent.id} value={agent.id}>
                        {agent.nombre} ({agent.tipo}) - {agent.correo}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Filtros */}
          <div className="card mb-4">
            <div className="card-header">
              <h6><i className="fas fa-filter me-2"></i>Filtros de Propiedades</h6>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-3">
                  <label className="form-label">Buscar:</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Nombre, ciudad, list number..."
                    value={filtro}
                    onChange={(e) => setFiltro(e.target.value)}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Ciudad:</label>
                  <select
                    className="form-select"
                    value={filtroCiudad}
                    onChange={(e) => setFiltroCiudad(e.target.value)}
                  >
                    <option value="">Todas</option>
                    {getCiudadesUnicas().map(ciudad => (
                      <option key={ciudad} value={ciudad}>{ciudad}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Tipo:</label>
                  <select
                    className="form-select"
                    value={filtroTipo}
                    onChange={(e) => setFiltroTipo(e.target.value)}
                  >
                    <option value="">Todos</option>
                    {getTiposUnicos().map(tipo => (
                      <option key={tipo} value={tipo}>{tipo}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Estado:</label>
                  <select
                    className="form-select"
                    value={filtroEstado}
                    onChange={(e) => setFiltroEstado(e.target.value as any)}
                  >
                    <option value="todas">Todas las propiedades</option>
                    <option value="sin_agencia">⚠️ Sin agencia</option>
                    <option value="con_agencia">✅ Con agencia</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Acción Masiva:</label>
                  <button
                    className="btn btn-success w-100"
                    onClick={handleBulkAssign}
                    disabled={!selectedAgency || processing || propiedadesFiltradas.filter(p => !p.inmobiliaria && !p.inmobiliaria_id).length === 0}
                  >
                    {processing ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Asignando...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-link me-2"></i>
                        Asignar ({propiedadesFiltradas.filter(p => !p.inmobiliaria && !p.inmobiliaria_id).length})
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Estadísticas de filtrado */}
              <div className="mt-3">
                <div className="alert alert-light">
                  <div className="row text-center">
                    <div className="col-md-3">
                      <strong>{propiedadesFiltradas.length}</strong><br />
                      <small>Total Filtradas</small>
                    </div>
                    <div className="col-md-3">
                      <strong>{propiedadesFiltradas.filter(p => p.inmobiliaria || p.inmobiliaria_id).length}</strong><br />
                      <small className="text-success">Con Agencia</small>
                    </div>
                    <div className="col-md-3">
                      <strong>{propiedadesFiltradas.filter(p => !p.inmobiliaria && !p.inmobiliaria_id).length}</strong><br />
                      <small className="text-danger">Sin Agencia</small>
                    </div>
                    <div className="col-md-3">
                      <strong>{propiedadesFiltradas.filter(p => p.agente_id).length}</strong><br />
                      <small className="text-info">Con Agente</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabla de propiedades */}
          <div className="table-responsive">
            <table className="table table-striped">
              <thead className="table-dark">
                <tr>
                  <th>Nombre</th>
                  <th>List Number</th>
                  <th>Ciudad</th>
                  <th>Tipo</th>
                  <th>Precio</th>
                  <th>Agencia</th>
                  <th>Agente</th>
                  <th>Acción</th>
                </tr>
              </thead>
              <tbody>
                {propiedadesFiltradas.slice(0, 100).map(property => (
                  <tr key={property.id}>
                    <td>
                      <strong>{property.nombre}</strong>
                    </td>
                    <td>
                      <code>{property.list_number || '-'}</code>
                    </td>
                    <td>{property.ciudad || '-'}</td>
                    <td>
                      <span className="badge bg-secondary">
                        {property.tipo || 'Sin tipo'}
                      </span>
                    </td>
                    <td>
                      {property.precio ? `$${property.precio.toLocaleString()}` : '-'}
                    </td>
                    <td>
                      {property.inmobiliaria ? (
                        <span className="badge bg-success">
                          {property.inmobiliaria}
                        </span>
                      ) : (
                        <span className="badge bg-danger">Sin agencia</span>
                      )}
                    </td>
                    <td>
                      {property.agente_nombre ? (
                        <small>{property.agente_nombre}</small>
                      ) : (
                        <small className="text-muted">Sin agente</small>
                      )}
                    </td>
                    <td>
                      {!property.inmobiliaria && !property.inmobiliaria_id ? (
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => handleAssignSingle(property)}
                          disabled={!selectedAgency || processing}
                        >
                          <i className="fas fa-link me-1"></i>
                          Asignar
                        </button>
                      ) : (
                        <button
                          className="btn btn-sm btn-outline-warning"
                          onClick={() => handleAssignSingle(property)}
                          disabled={!selectedAgency || processing}
                        >
                          <i className="fas fa-edit me-1"></i>
                          Cambiar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {propiedadesFiltradas.length > 100 && (
              <div className="alert alert-info">
                Mostrando solo las primeras 100 propiedades de {propiedadesFiltradas.length} totales
              </div>
            )}
          </div>

          {/* Información */}
          <div className="alert alert-info mt-4">
            <h6><i className="fas fa-info-circle me-2"></i>Información</h6>
            <ul className="mb-0">
              <li>Selecciona una agencia antes de asignar propiedades</li>
              <li>Opcionalmente puedes seleccionar un agente específico</li>
              <li>La asignación masiva solo afecta a propiedades sin agencia</li>
              <li>Puedes cambiar la asignación de propiedades ya asignadas</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}